import { CALL_DATA } from './callData.js';
// Real content for the landing: the 13 lessons of the current catalogue, the
// strategies they practise, and the AAPL call spread the page follows from the
// trade date to expiry. Prices come from the lessons and the app; titles and
// strikes match lib/features/learning/domain/tickerback_learning_catalog.dart
// and assets/l10n/learning_es.json.

/* --------------------------------------------------------------- course */
// The course is one path; the four groups follow the catalogue order.
export const MODULES = [
  {
    id: 'shares',
    es: { title: 'Antes de las opciones', summary: 'Compras acciones y ves qué hace el tiempo con ellas.' },
    en: { title: 'Before options', summary: 'You buy shares and watch what time does to them.' },
    lessons: [1, 2],
  },
  {
    id: 'protect',
    es: { title: 'Proteger y cobrar', summary: 'Tu primera put, tu primera call vendida y lo que cuesta cada una.' },
    en: { title: 'Protect and collect', summary: 'Your first put, your first sold call and what each one costs.' },
    lessons: [3, 4, 5, 6, 7],
  },
  {
    id: 'shapes',
    es: { title: 'Estrategias con forma', summary: 'Dos patas a la vez: spreads, straddles y strangles.' },
    en: { title: 'Strategies with a shape', summary: 'Two legs at once: spreads, straddles and strangles.' },
    lessons: [8, 9, 10, 11],
  },
  {
    id: 'thesis',
    es: { title: 'Tu propia tesis', summary: 'Decides qué esperas, lo montas y se lo das a alguien para que lo compruebe.' },
    en: { title: 'Your own thesis', summary: 'You decide what you expect, build it and hand it to someone who can check it.' },
    lessons: [12, 13],
  },
];

export const LESSONS = [
  { n: 1, id: 'oc-own-shares', minutes: 6, es: 'Compra 100 acciones y deja pasar el tiempo', en: 'Buy 100 shares and let time pass' },
  { n: 2, id: 'oc-own-drawdown', minutes: 6, es: 'Las mismas acciones, tres meses después', en: 'The same shares, three months later' },
  { n: 3, id: 'oc-protective-put', minutes: 8, es: 'Asegura las acciones con una opción de venta', en: 'Insure the shares with a Put' },
  { n: 4, id: 'oc-insurance-cost', minutes: 7, es: 'El seguro que no necesitaste', en: 'The insurance you did not need' },
  { n: 5, id: 'oc-covered-call', minutes: 7, es: 'Ahora el que cobra eres tú', en: 'Now you are the one being paid' },
  { n: 6, id: 'oc-option-alone', minutes: 8, es: 'Sin las acciones: la opción sola', en: 'Without the shares: the option on its own' },
  { n: 7, id: 'oc-collar', minutes: 10, es: 'Protección que se paga sola', en: 'Protection that pays for itself' },
  { n: 8, id: 'st-call-vertical', minutes: 10, es: 'Dos patas, y una forma que puedes ver', en: 'Two legs, and a shape you can see' },
  { n: 9, id: 'st-put-credit-spread', minutes: 9, es: 'Cobrar porque no pase nada', en: 'Getting paid for nothing happening' },
  { n: 10, id: 'st-earnings-straddle', minutes: 10, es: 'Acertar el movimiento y equivocarse con el precio', en: 'Right about the move, wrong about the price' },
  { n: 11, id: 'st-strangle', minutes: 9, es: 'Pagar menos exige un movimiento mayor', en: 'Pay less, need a larger move' },
  { n: 12, id: 'st-your-thesis', minutes: 12, es: 'Tu turno: di qué esperas', en: 'Your turn: say what you expect' },
  { n: 13, id: 'sh-publish-record', minutes: 6, es: 'Dáselo a alguien que pueda comprobarlo', en: 'Hand it to somebody who can check it' },
];

export const COURSE_MINUTES = LESSONS.reduce((total, lesson) => total + lesson.minutes, 0);

/* ----------------------------------------------------------- strategies */
// The strategy each lesson builds, with the strikes the lesson uses. The
// shapes are drawn at expiry; spot and premiums only frame the drawing.
export const STRATEGIES = [
  { id: 'shares', lesson: 1, spot: 181, legs: [{ type: 'stock', side: 1 }],
    es: { name: 'Comprar acciones', legs: '100 acciones de AAPL' }, en: { name: 'Buying shares', legs: '100 AAPL shares' } },
  { id: 'protective-put', lesson: 3, spot: 180, legs: [{ type: 'stock', side: 1 }, { type: 'put', side: 1, strike: 175 }],
    es: { name: 'Put protectora', legs: 'Acciones + put 175 comprada' }, en: { name: 'Protective put', legs: 'Shares + long put 175' } },
  { id: 'covered-call', lesson: 5, spot: 226, legs: [{ type: 'stock', side: 1 }, { type: 'call', side: -1, strike: 240 }],
    es: { name: 'Call cubierta', legs: 'Acciones + call 240 vendida' }, en: { name: 'Covered call', legs: 'Shares + short call 240' } },
  { id: 'long-call', lesson: 6, spot: 197, legs: [{ type: 'call', side: 1, strike: 200 }],
    es: { name: 'Call comprada', legs: 'Call 200 comprada' }, en: { name: 'Long call', legs: 'Long call 200' } },
  { id: 'collar', lesson: 7, spot: 226, legs: [{ type: 'stock', side: 1 }, { type: 'put', side: 1, strike: 210 }, { type: 'call', side: -1, strike: 240 }],
    es: { name: 'Collar', legs: 'Acciones + put 210 + call 240 vendida' }, en: { name: 'Collar', legs: 'Shares + put 210 + short call 240' } },
  { id: 'call-spread', lesson: 8, spot: 216.24, legs: [{ type: 'call', side: 1, strike: 220 }, { type: 'call', side: -1, strike: 235 }],
    es: { name: 'Call spread alcista', legs: 'Call 220 comprada + call 235 vendida' }, en: { name: 'Bull call spread', legs: 'Long call 220 + short call 235' } },
  { id: 'put-credit', lesson: 9, spot: 216.24, legs: [{ type: 'put', side: -1, strike: 215 }, { type: 'put', side: 1, strike: 205 }],
    es: { name: 'Put spread de crédito', legs: 'Put 215 vendida + put 205 comprada' }, en: { name: 'Bull put credit spread', legs: 'Short put 215 + long put 205' } },
  { id: 'straddle', lesson: 10, spot: 230, legs: [{ type: 'call', side: 1, strike: 230 }, { type: 'put', side: 1, strike: 230 }],
    es: { name: 'Straddle', legs: 'Call 230 + put 230 compradas' }, en: { name: 'Long straddle', legs: 'Long call 230 + long put 230' } },
  { id: 'strangle', lesson: 11, spot: 230, legs: [{ type: 'put', side: 1, strike: 220 }, { type: 'call', side: 1, strike: 240 }],
    es: { name: 'Strangle', legs: 'Put 220 + call 240 compradas' }, en: { name: 'Long strangle', legs: 'Long put 220 + long call 240' } },
];

/* ------------------------------------------------------------ formatting */
const MONTHS = {
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

export function formatDate(d, lang = 'es') {
  const date = typeof d === 'string' ? new Date(`${d}T12:00:00Z`) : d;
  const month = MONTHS[lang === 'en' ? 'en' : 'es'][date.getUTCMonth()];
  return lang === 'en'
    ? `${month} ${date.getUTCDate()}, ${date.getUTCFullYear()}`
    : `${date.getUTCDate()} ${month} ${date.getUTCFullYear()}`;
}

// Money as the app shows it: Spanish "1.500 $" or English "$1,500".
export function formatUsd(value, { sign = false, decimals = 0, lang = 'es' } = {}) {
  // the sign follows the rounded figure, so nothing shows as "−0 $" or "+0 $"
  const abs = Math.abs(Number(value.toFixed(decimals)));
  const prefix = abs === 0 ? '' : value < 0 ? '−' : sign ? '+' : '';
  if (lang === 'en') {
    const text = abs.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `${prefix}$${text}`;
  }
  const text = abs.toLocaleString('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });
  // es-ES skips the thousands dot for four-digit numbers; keep it for money
  const grouped = abs >= 1000 && abs < 10000 && !text.includes('.') ? text.replace(/^(\d)(\d{3})/, '$1.$2') : text;
  return `${prefix}${grouped} $`;
}

/* ---------------------------------------------------------- Black-Scholes */
function ncdf(x) {
  // Abramowitz-Stegun 7.1.26, max error 1.5e-7
  const t = 1 / (1 + 0.3275911 * Math.abs(x) / Math.SQRT2);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-(x * x) / 2);
  return x >= 0 ? 0.5 * (1 + y) : 0.5 * (1 - y);
}

export function bs(type, S, K, T, r, q, v) {
  if (T <= 1e-6) return type === 'call' ? Math.max(0, S - K) : Math.max(0, K - S);
  const sq = v * Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r - q + 0.5 * v * v) * T) / sq;
  const d2 = d1 - sq;
  if (type === 'call') return S * Math.exp(-q * T) * ncdf(d1) - K * Math.exp(-r * T) * ncdf(d2);
  return K * Math.exp(-r * T) * ncdf(-d2) - S * Math.exp(-q * T) * ncdf(-d1);
}

// Value of a strategy at expiry per share, premiums priced once at a generic
// volatility: enough to draw the shape of the strategy without claiming prices.
export function strategyShape(strategy, points = 64) {
  const strikes = strategy.legs.map((leg) => leg.strike).filter(Boolean);
  const lo = Math.min(strategy.spot, ...strikes) * 0.86;
  const hi = Math.max(strategy.spot, ...strikes) * 1.14;
  const T = 45 / 365;
  const premium = strategy.legs.map((leg) =>
    leg.type === 'stock' ? strategy.spot : bs(leg.type, strategy.spot, leg.strike, T, 0.05, 0, 0.26),
  );
  const out = [];
  for (let i = 0; i < points; i++) {
    const S = lo + ((hi - lo) * i) / (points - 1);
    let pnl = 0;
    strategy.legs.forEach((leg, k) => {
      const atExpiry = leg.type === 'stock' ? S : leg.type === 'call' ? Math.max(0, S - leg.strike) : Math.max(0, leg.strike - S);
      pnl += leg.side * (atExpiry - premium[k]);
    });
    out.push([S, pnl]);
  }
  return { points: out, lo, hi, strikes, spot: strategy.spot };
}

/* ------------------------------------------------------ the call spread */
// Implied volatilities calibrated so Black-Scholes reproduces each premium on
// the entry day (dividend yield 0.45 %, calendar-day T): the entry P/L is 0 $.
const DAY = 86400000;
export const SPREAD = {
  ticker: 'AAPL',
  entry: '2024-08-09',
  expiry: '2024-09-20',
  spot: 216.24,
  close: 228.2,
  r: 0.052,
  q: 0.0045,
  debit: 500,
  maxProfit: 500,
  breakeven: 220,
  legs: [
    { type: 'call', side: 1, strike: 215, price: 8.9, iv: 0.2626 },
    { type: 'call', side: -1, strike: 225, price: 3.9, iv: 0.2364 },
  ],
  calendarDays: (Date.parse('2024-09-20') - Date.parse('2024-08-09')) / DAY,
  // AAPL's 30 real closes from the trade date to the expiry (Intrinio, as the app loads them)
  sessions: [
    ['2024-08-09', 216.24], ['2024-08-12', 217.53], ['2024-08-13', 221.27], ['2024-08-14', 221.72],
    ['2024-08-15', 224.72], ['2024-08-16', 226.05], ['2024-08-19', 225.89], ['2024-08-20', 226.51],
    ['2024-08-21', 226.4], ['2024-08-22', 224.53], ['2024-08-23', 226.84], ['2024-08-26', 227.18],
    ['2024-08-27', 228.03], ['2024-08-28', 226.49], ['2024-08-29', 229.79], ['2024-08-30', 229],
    ['2024-09-03', 222.77], ['2024-09-04', 220.85], ['2024-09-05', 222.38], ['2024-09-06', 220.82],
    ['2024-09-09', 220.91], ['2024-09-10', 220.11], ['2024-09-11', 222.66], ['2024-09-12', 222.77],
    ['2024-09-13', 222.5], ['2024-09-16', 216.32], ['2024-09-17', 216.79], ['2024-09-18', 220.69],
    ['2024-09-19', 228.87], ['2024-09-20', 228.2],
  ],
  // estimated P/L of the spread at each session's close (Black-Scholes, as in the diagram)
  sessionPnl() {
    const t0 = Date.parse(`${this.entry}T20:00:00Z`);
    const span = this.calendarDays * DAY;
    return this.sessions.map(([date, close]) => {
      const g = Math.min(1, (Date.parse(`${date}T20:00:00Z`) - t0) / span);
      return { date, close, pnl: this.pnlAt(close, g) };
    });
  },
  // P/L of the whole position (one contract per leg) at price S, with g the
  // share of the time to expiry already gone: 0 on the entry day, 1 at expiry.
  pnlAt(S, g) {
    const T = (this.calendarDays * (1 - g)) / 365;
    return this.legs.reduce(
      (total, leg) => total + leg.side * 100 * (bs(leg.type, S, leg.strike, T, this.r, this.q, leg.iv) - leg.price),
      0,
    );
  },
  dateAt(g) {
    return new Date(Date.parse(`${this.entry}T12:00:00Z`) + g * this.calendarDays * DAY);
  },
};

/* ------------------------------------------------ the 215 call, day by day */
// The call the page follows: AAPL 215, expiry 20 Sep 2024, bought on 9 Aug at the
// ask. Each session's result is what the chain paid for it (its bid); on the
// expiry day, its intrinsic value. Real prices, read from the platform.
export const CALL = { strike: 215, paid: 8.9, buy: '2024-08-09', sell: '2024-08-29' };
export function callDays() {
  const k = CALL_DATA.strikes.indexOf(CALL.strike);
  const close = Object.fromEntries(CALL_DATA.bars.map((b) => [b[0], b[4]]));
  return CALL_DATA.sessions.map((date, i) => {
    const S = close[date];
    const value = date === CALL_DATA.expiry ? Math.max(0, S - CALL.strike) : CALL_DATA.chain[i][k][0];
    return { date, close: S, value, pnl: (value - CALL.paid) * 100 };
  });
}
